import firebase from '../config/firebase';
import {
    LEGACY_SUBSCRIPTIONS_COLLECTION,
    SUBSCRIPTIONS_COLLECTION,
    mergePackageSubscriptionRows,
    normalizeLegacySubscriptionDocument,
    normalizeSubscriptionDocument,
} from './packageSubscriptionMapper';

const queryForYear = (collection, field, year) => {
    const db = firebase.firestore();
    let query = db.collection(collection);
    if (year !== '') query = query.where(field, '==', collection === SUBSCRIPTIONS_COLLECTION ? Number(year) : String(year));
    return query;
};

export function listenToPackageSubscriptions(year, packageFilter, onChange) {
    let primaryRows = [];
    let legacyRows = [];
    let primaryReady = false;
    let legacyReady = false;
    let primaryError = null;
    let legacyError = null;

    const emit = () => {
        if ((!primaryReady && !primaryError) || (!legacyReady && !legacyError)) return;
        const rows = mergePackageSubscriptionRows(primaryRows, legacyRows, packageFilter);
        onChange(rows, {
            primaryCount: rows.filter(row => row.sourceCollection === SUBSCRIPTIONS_COLLECTION).length,
            legacyFallbackCount: rows.filter(row => row.sourceCollection === LEGACY_SUBSCRIPTIONS_COLLECTION).length,
            primaryAvailable: !primaryError,
            primaryError,
            legacyError,
        });
    };

    const unsubscribePrimary = queryForYear(SUBSCRIPTIONS_COLLECTION, 'planYear', year).onSnapshot(snapshot => {
        primaryRows = snapshot.docs.map(doc => normalizeSubscriptionDocument(doc.id, doc.data()));
        primaryReady = true;
        primaryError = null;
        emit();
    }, error => {
        console.error('Greška pri dohvaćanju subscriptions kolekcije:', error);
        primaryRows = [];
        primaryReady = true;
        primaryError = error;
        emit();
    });

    const unsubscribeLegacy = queryForYear(LEGACY_SUBSCRIPTIONS_COLLECTION, 'year', year).onSnapshot(snapshot => {
        legacyRows = snapshot.docs.map(doc => normalizeLegacySubscriptionDocument(doc.id, doc.data()));
        legacyReady = true;
        legacyError = null;
        emit();
    }, error => {
        console.error('Greška pri dohvaćanju package_subscriptions kolekcije:', error);
        legacyRows = [];
        legacyReady = true;
        legacyError = error;
        emit();
    });

    return () => {
        unsubscribePrimary();
        unsubscribeLegacy();
    };
}

export async function getPackageSubscription(id) {
    const documentId = String(id);
    const db = firebase.firestore();

    try {
        const primary = await db.collection(SUBSCRIPTIONS_COLLECTION).doc(documentId).get();
        if (primary.exists) {
            const primaryRow = normalizeSubscriptionDocument(primary.id, primary.data());
            if (primaryRow.data.customer_type !== 'unknown') return primaryRow;

            const legacyDocumentId = primaryRow.data.legacyDocumentId || documentId;
            const legacy = await db.collection(LEGACY_SUBSCRIPTIONS_COLLECTION).doc(legacyDocumentId).get();
            if (!legacy.exists) return primaryRow;
            return mergePackageSubscriptionRows(
                [primaryRow],
                [normalizeLegacySubscriptionDocument(legacy.id, legacy.data())]
            )[0];
        }
    } catch (error) {
        console.error('Greška pri dohvaćanju subscriptions dokumenta:', error);
    }

    const legacy = await db.collection(LEGACY_SUBSCRIPTIONS_COLLECTION).doc(documentId).get();
    if (!legacy.exists) return null;
    return normalizeLegacySubscriptionDocument(legacy.id, legacy.data());
}

const normalizedFieldPaths = {
    user: 'customerSnapshot.name',
    company: 'customerSnapshot.company',
    address: 'customerSnapshot.address',
    city: 'customerSnapshot.city',
    phone: 'customerSnapshot.phone',
    zip: 'customerSnapshot.zip',
    invoiceId: 'invoice.invoiceId',
    description: 'description',
    isNewUser: 'isNewUser',
    customer_type: 'customerSnapshot.customerType',
};

export async function updatePackageSubscription(id, sourceCollection, changes) {
    const db = firebase.firestore();
    const documentId = String(id);

    if (sourceCollection === LEGACY_SUBSCRIPTIONS_COLLECTION) {
        const { customer_type: customerType, ...legacyChanges } = changes;
        if (customerType === 'person' || customerType === 'company') {
            legacyChanges.fizicko_lice = customerType === 'person';
        }
        return db.collection(LEGACY_SUBSCRIPTIONS_COLLECTION).doc(documentId).update(legacyChanges);
    }

    const updates = {};
    Object.entries(changes).forEach(([field, value]) => {
        const fieldPath = normalizedFieldPaths[field];
        if (fieldPath && (field !== 'customer_type' || value === 'person' || value === 'company')) {
            updates[fieldPath] = value;
        }
    });

    if (Object.prototype.hasOwnProperty.call(changes, 'email')) {
        updates.emailNormalized = String(changes.email || '').trim().toLowerCase();
        updates['customerSnapshot.email'] = String(changes.email || '').trim();
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'paid')) {
        updates.paid = changes.paid === true;
        updates.status = changes.paid === true ? 'active' : 'pending_payment';
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'paid_date')) {
        updates.paidAt = changes.paid_date || null;
    }
    updates.updatedAt = new Date().toISOString();

    return db.collection(SUBSCRIPTIONS_COLLECTION).doc(documentId).update(updates);
}

export async function deletePackageSubscription(id, sourceCollection) {
    return firebase.firestore()
        .collection(sourceCollection === LEGACY_SUBSCRIPTIONS_COLLECTION ? LEGACY_SUBSCRIPTIONS_COLLECTION : SUBSCRIPTIONS_COLLECTION)
        .doc(String(id))
        .delete();
}
